/**
 * Vocabulário da trilha de auditoria.
 *
 * A trilha é append-only: o que foi gravado fica como está, para sempre. E o
 * que foi gravado usa mais de um nome para o mesmo acontecimento — a migração
 * do sistema antigo trouxe `task.created`, `relatorio.created` e
 * `auth.login_success`, enquanto o sistema atual grava `tarefa.created`,
 * `report.published` e `auth.login`.
 *
 * Sem tratamento, a tela de auditoria oferece os dois nomes como filtros
 * separados e quem filtra por um perde o outro. Corrigir os registros não é
 * opção: uma trilha que se reescreve não serve de trilha.
 *
 * Então a junção acontece na leitura. Cada acontecimento tem uma **chave
 * canônica** — a que o sistema grava hoje — e a lista de nomes que já foram
 * gravados para ele. A tela mostra um filtro por acontecimento, com o texto em
 * português; o servidor traduz esse filtro de volta para todos os nomes.
 *
 * **A chave canônica é sempre um nome que já existe**, nunca um terceiro
 * inventado para ficar bonito: inventar um seria adicionar à confusão que este
 * arquivo existe para resolver.
 */

export interface AuditAction {
  /** O nome que o sistema grava hoje. */
  readonly key: string;
  /** Como se lê na tela. */
  readonly label: string;
  /** Nomes já gravados para o mesmo acontecimento, incluindo a própria chave. */
  readonly aliases: readonly string[];
}

/**
 * Um acontecimento por linha.
 *
 * `aliases` traz sempre a chave em primeiro; os demais são heranças da
 * migração ou de versões anteriores do sistema.
 */
export const AUDIT_ACTIONS: readonly AuditAction[] = [
  // ── acesso ────────────────────────────────────────────────────────
  { key: 'auth.login', label: 'Entrou no sistema', aliases: ['auth.login', 'auth.login_success'] },
  {
    key: 'auth.login_failure',
    label: 'Entrada recusada',
    aliases: ['auth.login_failure', 'auth.login_failed'],
  },
  { key: 'auth.logout', label: 'Saiu do sistema', aliases: ['auth.logout'] },
  {
    key: 'auth.password_changed',
    label: 'Trocou a própria senha',
    aliases: ['auth.password_changed', 'settings.password_changed'],
  },
  {
    key: 'auth.password_reset',
    label: 'Senha redefinida por link',
    aliases: ['auth.password_reset'],
  },
  {
    key: 'auth.password_reset_requested',
    label: 'Pediu recuperação de senha',
    aliases: ['auth.password_reset_requested'],
  },
  // ── obras ─────────────────────────────────────────────────────────
  { key: 'obra.created', label: 'Obra cadastrada', aliases: ['obra.created'] },
  { key: 'obra.updated', label: 'Obra alterada', aliases: ['obra.updated'] },
  { key: 'obra.deleted', label: 'Obra removida', aliases: ['obra.deleted'] },
  { key: 'obra.concluded', label: 'Obra concluída', aliases: ['obra.concluded'] },
  { key: 'obra.stopped', label: 'Obra paralisada', aliases: ['obra.stopped'] },
  { key: 'obra.resumed', label: 'Obra retomada', aliases: ['obra.resumed'] },
  { key: 'obra.fiscal_assigned', label: 'Fiscal designado', aliases: ['obra.fiscal_assigned'] },
  { key: 'obra.fiscal_removed', label: 'Fiscal retirado', aliases: ['obra.fiscal_removed'] },

  // ── relatórios e razão ────────────────────────────────────────────
  {
    key: 'report.published',
    label: 'Relatório publicado',
    aliases: ['report.published', 'relatorio.created'],
  },
  {
    key: 'report.updated',
    label: 'Relatório alterado',
    aliases: ['report.updated', 'relatorio.updated'],
  },
  { key: 'report.corrected', label: 'Relatório corrigido', aliases: ['report.corrected'] },
  { key: 'report.deleted', label: 'Relatório removido', aliases: ['report.deleted'] },
  { key: 'report.restored', label: 'Relatório restaurado', aliases: ['report.restored'] },
  { key: 'report.media_attached', label: 'Foto anexada', aliases: ['report.media_attached'] },
  { key: 'movement.reversed', label: 'Movimentação estornada', aliases: ['movement.reversed'] },

  // ── tarefas ───────────────────────────────────────────────────────
  { key: 'tarefa.created', label: 'Tarefa criada', aliases: ['tarefa.created', 'task.created'] },
  { key: 'tarefa.updated', label: 'Tarefa alterada', aliases: ['tarefa.updated', 'task.updated'] },
  { key: 'tarefa.deleted', label: 'Tarefa excluída', aliases: ['tarefa.deleted', 'task.deleted'] },
  {
    key: 'tarefa.completed',
    label: 'Tarefa concluída',
    aliases: ['tarefa.completed', 'task.completed'],
  },
  {
    key: 'tarefa.participant_added',
    label: 'Pessoa incluída na tarefa',
    aliases: ['tarefa.participant_added'],
  },
  {
    key: 'tarefa.participant_removed',
    label: 'Pessoa retirada da tarefa',
    aliases: ['tarefa.participant_removed'],
  },
  {
    key: 'tarefa.extension_requested',
    label: 'Prorrogação pedida',
    aliases: ['tarefa.extension_requested', 'task.extension_requested'],
  },
  { key: 'quadro.created', label: 'Quadro criado', aliases: ['quadro.created'] },
  { key: 'quadro.updated', label: 'Quadro alterado', aliases: ['quadro.updated'] },
  { key: 'quadro.deleted', label: 'Quadro apagado', aliases: ['quadro.deleted'] },
  { key: 'quadro.archived', label: 'Quadro arquivado', aliases: ['quadro.archived'] },

  // ── pessoas ───────────────────────────────────────────────────────
  {
    key: 'usuario.created',
    label: 'Usuário cadastrado',
    aliases: ['usuario.created', 'user.created'],
  },
  {
    key: 'usuario.updated',
    label: 'Acesso alterado',
    aliases: ['usuario.updated', 'user.updated'],
  },
  {
    key: 'usuario.removed',
    label: 'Vínculo removido',
    aliases: ['usuario.removed', 'user.removed', 'user.deleted'],
  },
  {
    key: 'usuario.password_reset',
    label: 'Senha redefinida pelo gestor',
    aliases: ['usuario.password_reset', 'user.password_reset', 'usuario.password_link'],
  },
  {
    key: 'usuario.profile_updated',
    label: 'Perfil alterado',
    aliases: ['settings.profile_updated', 'usuario.profile_updated'],
  },

  // ── cadastros e arquivos ──────────────────────────────────────────
  { key: 'company.created', label: 'Empresa cadastrada', aliases: ['company.created'] },
  { key: 'company.updated', label: 'Empresa alterada', aliases: ['company.updated'] },
  { key: 'company.deleted', label: 'Empresa removida', aliases: ['company.deleted'] },
  {
    key: 'secretaria.created',
    label: 'Secretaria cadastrada',
    aliases: ['secretaria.created'],
  },
  { key: 'secretaria.updated', label: 'Secretaria alterada', aliases: ['secretaria.updated'] },
  { key: 'secretaria.deleted', label: 'Secretaria removida', aliases: ['secretaria.deleted'] },
  { key: 'media.uploaded', label: 'Arquivo enviado', aliases: ['media.uploaded'] },
  {
    key: 'documento.created',
    label: 'Arquivo anexado à obra',
    aliases: ['documento.created'],
  },
  { key: 'documento.updated', label: 'Arquivo da obra alterado', aliases: ['documento.updated'] },
  {
    key: 'documento.deleted',
    label: 'Arquivo retirado do dossiê',
    aliases: ['documento.deleted'],
  },

  // ── plataforma ────────────────────────────────────────────────────
  { key: 'organizacao.created', label: 'Órgão cadastrado', aliases: ['organizacao.created'] },
  { key: 'organizacao.updated', label: 'Órgão alterado', aliases: ['organizacao.updated'] },
  {
    key: 'plataforma.password_link',
    label: 'Link de senha gerado (plataforma)',
    aliases: ['plataforma.password_link'],
  },
  {
    key: 'plataforma.user_status',
    label: 'Situação alterada (plataforma)',
    aliases: ['plataforma.user_status'],
  },
];

const POR_ALIAS = new Map<string, AuditAction>();
for (const acao of AUDIT_ACTIONS) {
  for (const alias of acao.aliases) POR_ALIAS.set(alias, acao);
}

/**
 * A chave canônica de um verbo gravado.
 *
 * Verbo desconhecido volta como está: a trilha pode conter registro de uma
 * versão que este catálogo não conhece, e escondê-lo seria pior do que
 * mostrá-lo cru.
 */
export function canonicalAuditAction(action: string): string {
  return POR_ALIAS.get(action)?.key ?? action;
}

/** O texto em português; o próprio verbo quando não se conhece. */
export function auditActionLabel(action: string): string {
  return POR_ALIAS.get(action)?.label ?? action;
}

/**
 * Todos os nomes já gravados para um acontecimento.
 *
 * É o que transforma um filtro escolhido na tela na lista que o `where` usa —
 * sem isto, filtrar "Tarefa criada" perderia os 28 registros que a migração
 * gravou como `task.created`.
 */
export function auditActionAliases(key: string): readonly string[] {
  return POR_ALIAS.get(key)?.aliases ?? [key];
}
